import * as Styled from './styled'
import { Popup, SlideUpHeaderBackButton } from 'common-components'
import SlideUp from 'common-components/SlideUp'
import ActionsOverlay from 'common-components/ActionsOverlay'
import { formatVideoDuration } from 'components/RecentlyUsed/VideoItem/utils'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { useScrollableElement } from 'containers/SubSidebar/AIVideoContent/context'
import { useResponsive } from 'hooks/useResponsive'
import { memo, useState, useRef, useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { AIVideoSocketMessageStatus, IAIVideoProject } from 'types/aiVideo'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { useAiLibVideoActions } from '../hooks/useAiLibVideoActions'
import AISkeletonItem from 'components/AISkeletonItem'
import { LoadingGradient } from 'common-components/LoadingGradient'
import { useGenerationProgress } from 'hooks/useGenerationProgress'
import VideoDetailsContent from './VideoDetailsContent'
import PreviewActionButtons from './PreviewActionButtons'
import { CONTEXT_MENU_OFFSET } from '../../consts'
import { PopupNavArrows, SlideUpNavArrows } from '../../NavArrows'
import PreviewIcon from 'assets/svgr-icons/Preview.svg'
import { useSidebarItemDrag } from 'hooks/useSidebarItemDrag'
import { convertAiVideoToBaseMediaItem } from 'containers/SubSidebar/AIVideoContent/HistoryTab/utils'

interface IVideoItemProps {
  item: IAIVideoProject
  index: number
  isPreviewOpen: boolean
  onOpenPreview: (index: number) => void
  onClosePreview: () => void
  onNext: () => void
  onPrev: () => void
  hasNext: boolean
  hasPrev: boolean
}

const VideoItem = memo(function VideoItem({
  item,
  index,
  isPreviewOpen,
  onOpenPreview,
  onClosePreview,
  onNext,
  onPrev,
  hasNext,
  hasPrev
}: IVideoItemProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)
  const [isPreviewVideoLoaded, setIsPreviewVideoLoaded] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const { scrollableElementRef } = useScrollableElement()
  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const aiVideoModels = useSelector(aiVideoSelectors.aiVideoModels)
  const isProcessing = item.status === AIVideoSocketMessageStatus.PROCESSING
  const { progress } = useGenerationProgress({
    assetId: item.id,
    isGenerating: isProcessing
  })
  const { setNodeRef, listeners, attributes, style, isDragging } =
    useSidebarItemDrag(
      isProcessing ? null : convertAiVideoToBaseMediaItem(item)
    )
  useEffect(() => {
    if (isPreviewOpen) {
      setIsPreviewVideoLoaded(false)
    }
  }, [isPreviewOpen, item.videoUrl])

  const openPreview = useCallback(
    () => onOpenPreview(index),
    [onOpenPreview, index]
  )
  const closePreview = onClosePreview

  const {
    handleAddToTimeline,
    handleRegenerate,
    handleDownload,
    isAiGenerated,
    ...actionsData
  } = useAiLibVideoActions({
    video: item,
    onClosePreview: closePreview
  })

  const handleItemClick = useCallback(() => {
    if (isPreviewOpen || isProcessing || isDragging) {
      return
    }
    handleAddToTimeline()
  }, [isPreviewOpen, isProcessing, handleAddToTimeline, isDragging])

  const handleMouseEnter = useCallback(() => {
    if (isMobile || isPreviewOpen || isDragging) return
    setIsHovered(true)
  }, [isMobile, isPreviewOpen, isDragging])

  const handleMouseLeave = useCallback(() => {
    if (isMobile || isDragging) return
    setIsHovered(false)
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }, [isMobile, isDragging])

  const handleVideoLoaded = useCallback(() => {
    setIsVideoLoaded(true)
    if (videoRef.current) {
      videoRef.current.play().catch(() => {})
    }
  }, [])

  const actionButtonProps = {
    onRegenerate: handleRegenerate,
    onDownload: handleDownload,
    onAddToTimeline: handleAddToTimeline,
    showRegenerate: isAiGenerated
  }

  if (isProcessing) {
    return (
      <Styled.Item>
        <AISkeletonItem />
        <Styled.ProcessingOverlay>
          <LoadingGradient progress={progress} size="small" />
        </Styled.ProcessingOverlay>
      </Styled.Item>
    )
  }

  return (
    <>
      <Styled.Item
        onClick={handleItemClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={setNodeRef}
        style={style}
        {...listeners}
        {...attributes}
      >
        <Styled.VideoDuration>
          {formatVideoDuration(item.videoDurationSeconds)}
        </Styled.VideoDuration>
        {item.imageUrl ? (
          <Styled.ThumbnailImage
            src={item.imageUrl}
            $hidden={isHovered && isVideoLoaded}
            loading="lazy"
          />
        ) : (
          <Styled.FallbackVideo
            src={item.videoUrl}
            preload="metadata"
            muted
            playsInline
            $hidden={isHovered && isVideoLoaded}
            crossOrigin="anonymous"
          />
        )}
        {isHovered && !isDragging && (
          <Styled.PreviewVideo
            ref={videoRef}
            preload="auto"
            src={item.videoUrl}
            muted
            onLoadedData={handleVideoLoaded}
            $isLoading={!isVideoLoaded}
            crossOrigin="anonymous"
          />
        )}
        {(isHovered || isMobile || (!item.imageUrl && !isDragging)) && (
          <>
            <ActionsOverlay
              actions={actionsData}
              scrollableElementRef={scrollableElementRef}
              contextMenuPosition={{
                flyoutOptions: {
                  position: 'bottom',
                  alignmentX: isMobile ? 'right' : 'left',
                  extraOffset: CONTEXT_MENU_OFFSET
                },
                togglerSize: { width: 20, height: 20 },
                isMobileWithDrawer: true,
                showMobileBackButton: true
              }}
            />
            <Styled.PreviewButton
              onClick={e => {
                e.stopPropagation()
                openPreview()
              }}
            >
              <PreviewIcon />
            </Styled.PreviewButton>
          </>
        )}
      </Styled.Item>

      {isMobile ? (
        <SlideUp
          isOpen={isPreviewOpen}
          onClose={closePreview}
          bottomMargin={0}
          disableAnimation
          zIndex={18}
          minHeight="600px"
        >
          <SlideUpHeaderBackButton onBack={closePreview} />

          <Styled.SlideUpScrollable>
            <Styled.VideoPreviewWrapper>
              <SlideUpNavArrows
                onPrev={onPrev}
                onNext={onNext}
                hasPrev={hasPrev}
                hasNext={hasNext}
              />
              {!isPreviewVideoLoaded && (
                <Styled.PreviewSkeletonOverlay>
                  <AISkeletonItem />
                </Styled.PreviewSkeletonOverlay>
              )}
              <Styled.VideoPreviewVideo
                crossOrigin="anonymous"
                src={item.videoUrl}
                controls
                playsInline
                onLoadedData={() => setIsPreviewVideoLoaded(true)}
              />
            </Styled.VideoPreviewWrapper>
            <VideoDetailsContent item={item} aiVideoModels={aiVideoModels} />
          </Styled.SlideUpScrollable>
          <Styled.SlideUpActions>
            <PreviewActionButtons {...actionButtonProps} />
          </Styled.SlideUpActions>
        </SlideUp>
      ) : (
        <Popup isOpen={isPreviewOpen} onClose={closePreview} disableAnimation>
          <Styled.PreviewContentWrapper>
            <PopupNavArrows
              onPrev={onPrev}
              onNext={onNext}
              hasPrev={hasPrev}
              hasNext={hasNext}
            />
            <Styled.VideoPreviewWrapper>
              {!isPreviewVideoLoaded && (
                <Styled.PreviewSkeletonOverlay>
                  <AISkeletonItem />
                </Styled.PreviewSkeletonOverlay>
              )}
              <Styled.VideoPreviewVideo
                crossOrigin="anonymous"
                src={item.videoUrl}
                controls
                playsInline
                onLoadedData={() => setIsPreviewVideoLoaded(true)}
              />
            </Styled.VideoPreviewWrapper>
            <Styled.VideoPreviewConent>
              <VideoDetailsContent item={item} aiVideoModels={aiVideoModels} />
              <Styled.PopupActions>
                <PreviewActionButtons {...actionButtonProps} />
              </Styled.PopupActions>
            </Styled.VideoPreviewConent>
          </Styled.PreviewContentWrapper>
        </Popup>
      )}
    </>
  )
})

export default VideoItem
